// Consolidated employee data with proper structure
export interface Employee {
  id: string;
  name: string;
  email: string;
  department: string;
  jobTitle: string;
  managerId: string | null; // Will be linked to actual employee IDs
  employeeStatus: 'Active' | 'Remote' | 'In Office' | 'On Leave';
  lastActivity: string;
  hireDate: string;
  initials: string;
  skills?: string[];
  projects?: string[];
}

// Complete consolidated team data
export const employees: Employee[] = [
  // CEO
  {
    id: 'ceo-001',
    name: 'Jordan Smith',
    email: 'jordan.smith@theta.com',
    department: 'Executive',
    jobTitle: 'CEO',
    managerId: null,
    employeeStatus: 'Active',
    lastActivity: '1 hour ago',
    hireDate: 'January 1, 2020',
    initials: 'JS',
    skills: ['Leadership', 'Strategy', 'Vision'],
    projects: ['Company Strategy', 'Board Relations']
  },

  // Sales Team
  {
    id: 'vp-sales-001',
    name: 'Sarah Mitchell',
    email: 'sarah.mitchell@theta.com',
    department: 'Sales',
    jobTitle: 'VP of Sales',
    managerId: 'ceo-001',
    employeeStatus: 'Active',
    lastActivity: '1 hour ago',
    hireDate: 'August 15, 2020',
    initials: 'SM',
    skills: ['Sales Strategy', 'Team Leadership', 'Revenue Growth'],
    projects: ['Q4 Sales Strategy', 'Team Expansion']
  },
  {
    id: '1',
    name: 'Michael Thompson',
    email: 'michael.thompson@theta.com',
    department: 'Sales',
    jobTitle: 'Senior Account Executive',
    managerId: 'vp-sales-001',
    employeeStatus: 'Active',
    lastActivity: '2 hours ago',
    hireDate: 'January 15, 2023',
    initials: 'MT',
    skills: ['Enterprise Sales', 'Relationship Building', 'Negotiation'],
    projects: ['Enterprise Accounts', 'Key Account Management']
  },
  {
    id: '2',
    name: 'Alex Drago',
    email: 'alex.drago@theta.com',
    department: 'Sales',
    jobTitle: 'Account Executive',
    managerId: 'vp-sales-001',
    employeeStatus: 'Active',
    lastActivity: '1 day ago',
    hireDate: 'March 10, 2023',
    initials: 'AD',
    skills: ['B2B Sales', 'Lead Generation', 'CRM Management'],
    projects: ['Mid-Market Accounts', 'Sales Process Optimization']
  },
  {
    id: '3',
    name: 'Carol Carter',
    email: 'carol.carter@theta.com',
    department: 'Sales',
    jobTitle: 'Senior Account Executive',
    managerId: 'vp-sales-001',
    employeeStatus: 'Active',
    lastActivity: '3 hours ago',
    hireDate: 'June 5, 2023',
    initials: 'CC',
    skills: ['Strategic Sales', 'Account Planning', 'Team Mentoring'],
    projects: ['Strategic Accounts', 'Sales Training Program']
  },
  {
    id: '4',
    name: 'Frank Wright',
    email: 'frank.wright@theta.com',
    department: 'Sales',
    jobTitle: 'Account Executive',
    managerId: 'vp-sales-001',
    employeeStatus: 'Active',
    lastActivity: '5 minutes ago',
    hireDate: 'August 20, 2023',
    initials: 'FW',
    skills: ['New Business Development', 'Product Demos', 'Pipeline Management'],
    projects: ['New Logo Acquisition', 'Product Launch Support']
  },
  {
    id: '5',
    name: 'Sarah Mitchell Jr',
    email: 'sarah.mitchell.jr@theta.com',
    department: 'Sales',
    jobTitle: 'Sales Development Rep',
    managerId: '1', // Reports to Michael Thompson
    employeeStatus: 'Active',
    lastActivity: '1 hour ago',
    hireDate: 'October 1, 2023',
    initials: 'SMJ',
    skills: ['Lead Qualification', 'Cold Outreach', 'Sales Research'],
    projects: ['Lead Generation', 'Prospecting Automation']
  },
  {
    id: '6',
    name: 'Marcus Johnson',
    email: 'marcus.johnson@theta.com',
    department: 'Sales',
    jobTitle: 'Sales Development Rep',
    managerId: '3', // Reports to Carol Carter
    employeeStatus: 'Active',
    lastActivity: '3 hours ago',
    hireDate: 'November 15, 2023',
    initials: 'MJ',
    skills: ['Prospecting', 'Email Marketing', 'Data Analysis'],
    projects: ['SDR Process Improvement', 'Lead Scoring']
  },

  // Account Management Team
  {
    id: 'vp-customer-success-001',
    name: 'Amy McKay',
    email: 'amy.mckay@theta.com',
    department: 'Account Management',
    jobTitle: 'VP of Customer Success',
    managerId: 'ceo-001',
    employeeStatus: 'Active',
    lastActivity: '4 hours ago',
    hireDate: 'May 20, 2020',
    initials: 'AM',
    skills: ['Customer Success Strategy', 'Team Leadership', 'Retention'],
    projects: ['Customer Success Platform', 'Retention Strategy']
  },
  {
    id: '7',
    name: 'Jack Stunner',
    email: 'jack.stunner@theta.com',
    department: 'Account Management',
    jobTitle: 'Senior Account Manager',
    managerId: 'vp-customer-success-001',
    employeeStatus: 'Active',
    lastActivity: '1 hour ago',
    hireDate: 'September 12, 2022',
    initials: 'JS',
    skills: ['Account Management', 'Customer Expansion', 'Relationship Building'],
    projects: ['Enterprise Customer Expansion', 'Customer Health Scoring']
  },
  {
    id: '9',
    name: 'Cynthia Simons',
    email: 'cynthia.simons@theta.com',
    department: 'Account Management',
    jobTitle: 'Account Manager',
    managerId: 'vp-customer-success-001',
    employeeStatus: 'Active',
    lastActivity: '2 hours ago',
    hireDate: 'December 5, 2022',
    initials: 'CS',
    skills: ['Customer Onboarding', 'Support', 'Cross-selling'],
    projects: ['Onboarding Optimization', 'Customer Training Program']
  },

  // Marketing Team
  {
    id: 'vp-marketing-001',
    name: 'Lisa Chen',
    email: 'lisa.chen@theta.com',
    department: 'Marketing',
    jobTitle: 'VP of Marketing',
    managerId: 'ceo-001',
    employeeStatus: 'Active',
    lastActivity: '2 hours ago',
    hireDate: 'January 3, 2021',
    initials: 'LC',
    skills: ['Marketing Strategy', 'Brand Management', 'Digital Marketing'],
    projects: ['Brand Refresh', 'Marketing Automation', 'Lead Generation Strategy']
  },
  {
    id: '12',
    name: 'David Rodriguez',
    email: 'david.rodriguez@theta.com',
    department: 'Marketing',
    jobTitle: 'Content Marketing Specialist',
    managerId: 'vp-marketing-001',
    employeeStatus: 'Active',
    lastActivity: '5 hours ago',
    hireDate: 'May 20, 2023',
    initials: 'DR',
    skills: ['Content Creation', 'SEO', 'Blog Writing', 'Social Media'],
    projects: ['Content Calendar', 'SEO Optimization', 'Thought Leadership']
  },
  {
    id: '13',
    name: 'Emma Thompson',
    email: 'emma.thompson@theta.com',
    department: 'Marketing',
    jobTitle: 'Digital Marketing Specialist',
    managerId: 'vp-marketing-001',
    employeeStatus: 'Active',
    lastActivity: '1 day ago',
    hireDate: 'July 10, 2023',
    initials: 'ET',
    skills: ['PPC', 'Social Media Advertising', 'Analytics', 'Email Marketing'],
    projects: ['Paid Advertising', 'Marketing Analytics', 'Email Campaigns']
  },

  // Customer Success Team
  {
    id: '15',
    name: 'Jennifer Davis',
    email: 'jennifer.davis@theta.com',
    department: 'Customer Success',
    jobTitle: 'Director of Customer Success',
    managerId: 'vp-customer-success-001',
    employeeStatus: 'Active',
    lastActivity: '3 hours ago',
    hireDate: 'September 1, 2020',
    initials: 'JD',
    skills: ['Customer Success', 'Team Management', 'Process Improvement'],
    projects: ['Customer Success Operations', 'Success Metrics Dashboard']
  },
  {
    id: '14',
    name: 'Michael Brown',
    email: 'michael.brown@theta.com',
    department: 'Customer Success',
    jobTitle: 'Customer Success Manager',
    managerId: '15',
    employeeStatus: 'Active',
    lastActivity: '1 hour ago',
    hireDate: 'March 15, 2023',
    initials: 'MB',
    skills: ['Customer Onboarding', 'Product Training', 'Support'],
    projects: ['Customer Onboarding', 'Product Adoption', 'Success Planning']
  },

  // Operations Team
  {
    id: 'vp-operations-001',
    name: 'Jayson Oneil',
    email: 'jayson.oneil@theta.com',
    department: 'Operations',
    jobTitle: 'VP of Operations',
    managerId: 'ceo-001',
    employeeStatus: 'Active',
    lastActivity: '30 minutes ago',
    hireDate: 'February 14, 2023',
    initials: 'JO',
    skills: ['Operations Strategy', 'Process Optimization', 'Team Leadership'],
    projects: ['Operations Efficiency', 'Process Automation', 'Team Scaling']
  },

  // Product Team
  {
    id: 'vp-product-001',
    name: 'Rachel Green',
    email: 'rachel.green@theta.com',
    department: 'Product',
    jobTitle: 'VP of Product',
    managerId: 'ceo-001',
    employeeStatus: 'Active',
    lastActivity: '6 hours ago',
    hireDate: 'April 5, 2020',
    initials: 'RG',
    skills: ['Product Strategy', 'Roadmap Planning', 'Team Leadership'],
    projects: ['Product Roadmap', 'Feature Planning', 'User Research']
  },
  {
    id: '18',
    name: 'Kevin Lee',
    email: 'kevin.lee@theta.com',
    department: 'Product',
    jobTitle: 'Product Manager',
    managerId: 'vp-product-001',
    employeeStatus: 'Active',
    lastActivity: '1 hour ago',
    hireDate: 'February 20, 2023',
    initials: 'KL',
    skills: ['Product Management', 'Feature Development', 'User Stories'],
    projects: ['Core Platform Features', 'User Experience Improvements']
  },
  {
    id: '20',
    name: 'Thomas Anderson',
    email: 'thomas.anderson@theta.com',
    department: 'Product',
    jobTitle: 'Product Analyst',
    managerId: '18',
    employeeStatus: 'Active',
    lastActivity: '2 hours ago',
    hireDate: 'August 12, 2023',
    initials: 'TA',
    skills: ['Data Analysis', 'Product Analytics', 'User Research'],
    projects: ['Product Analytics', 'User Behavior Analysis', 'A/B Testing']
  }
];

// Helper functions for working with employee data
export const getEmployeeById = (id: string): Employee | undefined => {
  return employees.find(emp => emp.id === id);
};

export const getEmployeesByDepartment = (department: string): Employee[] => {
  return employees.filter(emp => emp.department === department);
};

export const getDirectReports = (managerId: string): Employee[] => {
  return employees.filter(emp => emp.managerId === managerId);
};

export const getManagerChain = (employeeId: string): Employee[] => {
  const chain: Employee[] = [];
  let currentEmployee = getEmployeeById(employeeId);
  
  while (currentEmployee && currentEmployee.managerId) {
    const manager = getEmployeeById(currentEmployee.managerId);
    if (manager) {
      chain.push(manager);
      currentEmployee = manager;
    } else {
      break;
    }
  }
  
  return chain;
};

export const getDepartments = (): string[] => {
  const departmentSet = new Set(employees.map(emp => emp.department));
  return Array.from(departmentSet);
};

export const getEmployeesByManager = (managerId: string): Employee[] => {
  return employees.filter(emp => emp.managerId === managerId);
};

// Organization statistics
export const getOrgStats = () => {
  const departments = getDepartments();
  const stats = {
    totalEmployees: employees.length,
    departmentCounts: {} as Record<string, number>,
    activeEmployees: employees.filter(emp => emp.employeeStatus === 'Active').length,
    avgTenure: 0 // Could calculate based on hire dates
  };

  departments.forEach(dept => {
    stats.departmentCounts[dept] = getEmployeesByDepartment(dept).length;
  });

  return stats;
};
